import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  EventNote,
  CheckCircle,
  Cancel,
  Schedule,
  LocationOn,
  AttachMoney,
  Share,
  MoreVert,
  CalendarToday,
  Refresh,
  QrCodeScanner,
  EditCalendar,
  Close,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import useAnalytics from '../../analytics/useAnalytics';
import { EVT_BOOKING_CANCEL } from '../../analytics/events';
import SurfaceCard from '../../components/shared/SurfaceCard';
import PillTabs from '../../components/shared/PillTabs';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';
import BottomSheet from '../../components/shared/BottomSheet';
import { RADIUS } from '../../theme/tokens';
import { listContainerVariants, listItemVariants } from '../../theme/motion';

const MotionBox = motion(Box);

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
];

const EMPTY_MESSAGES = {
  all: { title: 'No bookings yet', description: "You haven't made any bookings yet", action: 'Browse libraries' },
  active: { title: 'No active bookings', description: 'Browse libraries and book a seat', action: 'Browse libraries' },
  completed: { title: 'No completed bookings yet', description: null, action: null },
  cancelled: { title: "You haven't cancelled anything", description: null, action: null },
};

function getStatusColor(status) {
  switch (status) {
    case 'upcoming':
    case 'active':
      return 'primary';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'error';
    case 'no_show':
      return 'warning';
    default:
      return 'default';
  }
}

function getStatusIcon(status) {
  switch (status) {
    case 'completed':
      return <CheckCircle />;
    case 'cancelled':
      return <Cancel />;
    case 'upcoming':
    case 'active':
      return <Schedule />;
    default:
      return <Schedule />;
  }
}

/**
 * StudentBookings — booking list with PillTabs filter and per-filter empty states.
 * Analytics calls (EVT_BOOKING_CANCEL) preserved verbatim from the original.
 */
function StudentBookings({ showSnackbar, initialFilter }) {
  const { isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { track } = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = initialFilter && TABS.find(t => t.key === initialFilter) ? initialFilter : 'all';

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelSheetOpen, setCancelSheetOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [rescheduleSheetOpen, setRescheduleSheetOpen] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');

  const todayStr = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open detail from URL param
  useEffect(() => {
    const bookingId = searchParams.get('bookingId');
    if (bookingId && bookings.length > 0) {
      const booking = bookings.find(b => b.id === bookingId);
      if (booking) {
        setSelectedBooking(booking);
        setDetailOpen(true);
        searchParams.delete('bookingId');
        setSearchParams(searchParams);
      }
    }
  }, [bookings, searchParams, setSearchParams]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/bookings');
      setBookings(response.data.data.bookings || []);
    } catch {
      showSnackbar('Failed to load bookings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    try {
      setCancelling(true);
      await api.post(`/bookings/${selectedBooking.id}/cancel`);
      track(EVT_BOOKING_CANCEL, { bookingId: selectedBooking.id });
      showSnackbar('Booking cancelled successfully', 'success');
      setCancelSheetOpen(false);
      setSelectedBooking(null);
      fetchBookings();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to cancel booking', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const openRescheduleSheet = (booking) => {
    setSelectedBooking(booking);
    // Pre-fill with the current start date (or today) for convenience.
    setNewStartDate(booking.start_date ? booking.start_date.split('T')[0] : todayStr);
    setRescheduleSheetOpen(true);
  };

  const handleRescheduleBooking = async () => {
    if (!selectedBooking || !newStartDate) return;
    try {
      setRescheduling(true);
      await api.post(`/bookings/${selectedBooking.id}/reschedule`, { start_date: newStartDate });
      showSnackbar('Booking rescheduled successfully', 'success');
      setRescheduleSheetOpen(false);
      setSelectedBooking(null);
      fetchBookings();
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to reschedule booking', 'error');
    } finally {
      setRescheduling(false);
    }
  };

  const filterBookings = () => {
    if (activeTab === 'active') return bookings.filter(b => b.status === 'active' || b.status === 'upcoming');
    if (activeTab === 'completed') return bookings.filter(b => b.status === 'completed');
    if (activeTab === 'cancelled') return bookings.filter(b => b.status === 'cancelled');
    return bookings;
  };

  const filteredBookings = filterBookings();
  const empty = EMPTY_MESSAGES[activeTab] ?? EMPTY_MESSAGES.all;

  // Detail sheet / dialog content
  const detailContent = selectedBooking && (
    <>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Booking Details</Typography>
          <Typography variant="caption" color="text.secondary">#{selectedBooking.id.substring(0, 8)}</Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => setDetailOpen(false)}
          aria-label="Close booking details"
        >
          <Close />
        </IconButton>
      </Box>

      <Box sx={{ px: 2, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Library */}
        <Box sx={{ bgcolor: 'background.subtle', borderRadius: RADIUS.md, p: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>LIBRARY</Typography>
          <Typography variant="h6" fontWeight={700}>{selectedBooking.library?.name ?? 'Unknown Library'}</Typography>
          {selectedBooking.library?.address && (
            <Box display="flex" alignItems="flex-start" gap={0.5} mt={0.5}>
              <LocationOn sx={{ fontSize: 14, color: 'text.secondary', mt: 0.2 }} />
              <Typography variant="body2" color="text.secondary">{selectedBooking.library.address}</Typography>
            </Box>
          )}
        </Box>

        {/* Status */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>STATUS</Typography>
          <Chip
            icon={getStatusIcon(selectedBooking.status)}
            label={selectedBooking.status.replace('_', ' ').toUpperCase()}
            color={getStatusColor(selectedBooking.status)}
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Plan */}
        {selectedBooking.plan && (
          <SurfaceCard radius="md" elevation="xs" padding={2} sx={{ bgcolor: 'primary.soft', color: 'primary.dark' }}>
            <Typography variant="overline" display="block" sx={{ mb: 0.75 }}>Plan details</Typography>
            <Typography variant="h6" fontWeight={700} color="text.primary" mb={0.5}>{selectedBooking.plan.name}</Typography>
            <Box display="flex" gap={3}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Price</Typography>
                <Typography variant="body1" fontWeight={600} color="text.primary">₹{selectedBooking.plan.price_cents}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">Duration</Typography>
                <Typography variant="body1" fontWeight={600} color="text.primary">{selectedBooking.plan.duration_days} days</Typography>
              </Box>
            </Box>
          </SurfaceCard>
        )}

        {/* Booking period */}
        {(selectedBooking.start_date || selectedBooking.end_date) && (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>BOOKING PERIOD</Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              {selectedBooking.start_date && (
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">Start Date</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {new Date(selectedBooking.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>
              )}
              {selectedBooking.start_date && selectedBooking.end_date && (
                <Typography variant="h6" color="text.secondary">→</Typography>
              )}
              {selectedBooking.end_date && (
                <Box textAlign="right">
                  <Typography variant="caption" color="text.secondary" display="block">End Date</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {new Date(selectedBooking.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Created */}
        <Box>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>CREATED</Typography>
          <Typography variant="body2" fontWeight={600}>
            {new Date(selectedBooking.created_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </Typography>
        </Box>

        {/* Actions */}
        <Box display="flex" flexDirection="column" gap={1.5}>
          {selectedBooking.status === 'active' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<QrCodeScanner />}
              onClick={() => { setDetailOpen(false); navigate('/student/scanner'); }}
              sx={{ borderRadius: RADIUS.lg, py: 1.5, fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}
            >
              Scan QR to Enter Library
            </Button>
          )}
          {(selectedBooking.status === 'upcoming' || selectedBooking.status === 'pending') && (
            <Button
              variant="outlined"
              startIcon={<EditCalendar />}
              onClick={() => { setDetailOpen(false); openRescheduleSheet(selectedBooking); }}
              sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
            >
              Reschedule
            </Button>
          )}
          {(selectedBooking.status === 'upcoming' || selectedBooking.status === 'pending') && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => { setDetailOpen(false); setCancelSheetOpen(true); }}
              sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
            >
              Cancel Booking
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => setDetailOpen(false)}
            sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </>
  );

  // Reschedule sheet / dialog body (date picker + actions). Shared between the
  // mobile BottomSheet and desktop Dialog.
  const rescheduleContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...(isMobile ? {} : { pt: 1 }) }}>
      <Typography variant="body2" color="text.secondary">
        Pick a new start date for your booking.{' '}
        {selectedBooking?.plan?.duration_days
          ? `The end date will shift to keep the ${selectedBooking.plan.duration_days}-day plan length.`
          : ''}
      </Typography>
      {selectedBooking && (
        <Box p={1.5} sx={{ bgcolor: 'background.subtle', borderRadius: RADIUS.md }}>
          <Typography variant="subtitle2" fontWeight={600}>{selectedBooking.library?.name}</Typography>
          <Typography variant="caption" color="text.secondary">{selectedBooking.plan?.name}</Typography>
        </Box>
      )}
      <TextField
        label="New start date"
        type="date"
        value={newStartDate}
        onChange={(e) => setNewStartDate(e.target.value)}
        fullWidth
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: todayStr }}
      />
      <Box display="flex" gap={1.5}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => setRescheduleSheetOpen(false)}
          disabled={rescheduling}
          sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleRescheduleBooking}
          disabled={rescheduling || !newStartDate || newStartDate < todayStr}
          sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
        >
          {rescheduling ? 'Saving…' : 'Confirm'}
        </Button>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Container maxWidth="md" sx={{ py: 2 }}>
          <LoadingSkeleton variant="hero" sx={{ mb: 2 }} />
          <LoadingSkeleton variant="listItem" count={4} />
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 2 }}>
        <MotionBox
          initial="initial"
          animate="animate"
          variants={listContainerVariants}
        >
          {/* Header */}
          <MotionBox variants={listItemVariants} mb={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Box>
                <Typography variant="h2" fontWeight={700}>My bookings</Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredBookings.length} {filteredBookings.length === 1 ? 'booking' : 'bookings'}
                </Typography>
              </Box>
              <IconButton
                onClick={fetchBookings}
                aria-label="Refresh bookings"
                color="primary"
              >
                <Refresh />
              </IconButton>
            </Box>

            {/* PillTabs replacing MUI Tabs */}
            <PillTabs
              tabs={TABS}
              value={activeTab}
              onChange={setActiveTab}
              fullWidth
            />
          </MotionBox>

          {/* List */}
          {filteredBookings.length === 0 ? (
            <MotionBox variants={listItemVariants}>
              <EmptyState
                icon={<EventNote />}
                title={empty.title}
                description={empty.description}
                actionLabel={empty.action}
                onAction={empty.action ? () => navigate('/student/libraries') : undefined}
              />
            </MotionBox>
          ) : (
            <AnimatePresence mode="popLayout">
              {filteredBookings.map((booking) => (
                <MotionBox
                  key={booking.id}
                  variants={listItemVariants}
                  initial="initial"
                  animate="animate"
                  exit={{ opacity: 0, y: -4 }}
                  layout
                  whileTap={{ scale: 0.98 }}
                  onClick={() => { setSelectedBooking(booking); setDetailOpen(true); }}
                  sx={{ mb: 2, cursor: 'pointer' }}
                >
                  <SurfaceCard
                    radius="lg"
                    elevation="sm"
                    interactive
                    padding={2}
                    sx={{
                      border: booking.status === 'active'
                        ? `1.5px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    {/* Card header row */}
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Box display="flex" gap={1.5} alignItems="center" flex={1} minWidth={0}>
                        {/* Library avatar */}
                        <Avatar
                          sx={{
                            width: 44,
                            height: 44,
                            background: theme.palette.primary.main,
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            flexShrink: 0,
                          }}
                        >
                          {booking.library?.name?.charAt(0) ?? 'L'}
                        </Avatar>
                        <Box minWidth={0}>
                          <Typography variant="subtitle2" fontWeight={700} noWrap>
                            {booking.library?.name ?? 'Unknown Library'}
                          </Typography>
                          {booking.library?.address && (
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <LocationOn sx={{ fontSize: 12, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {booking.library.address.substring(0, 35)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={0.5} flexShrink={0}>
                        <Chip
                          icon={getStatusIcon(booking.status)}
                          label={booking.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(booking.status)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            showSnackbar('More options — Coming Soon!', 'info');
                          }}
                          aria-label="More booking options"
                        >
                          <MoreVert />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Plan info */}
                    {booking.plan && (
                      <Box sx={{ bgcolor: 'background.subtle', borderRadius: RADIUS.md, p: 1.5, mb: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
                          PLAN DETAILS
                        </Typography>
                        <Typography variant="subtitle2" fontWeight={600}>{booking.plan.name}</Typography>
                        <Box display="flex" gap={2} mt={0.5}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <AttachMoney sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">₹{booking.plan.price_cents}</Typography>
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="body2">{booking.plan.duration_days} days</Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Booking period */}
                    {(booking.start_date || booking.end_date) && (
                      <SurfaceCard
                        radius="sm"
                        elevation="xs"
                        padding={1.5}
                        sx={{ bgcolor: 'primary.soft', color: 'primary.dark', mb: 1.5 }}
                      >
                        <Typography variant="overline" display="block" sx={{ mb: 0.5 }}>
                          Booking period
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          {booking.start_date && (
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">Start</Typography>
                              <Typography variant="body2" fontWeight={700} color="text.primary">
                                {new Date(booking.start_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </Typography>
                            </Box>
                          )}
                          {booking.start_date && booking.end_date && (
                            <Typography variant="h6" color="text.secondary">→</Typography>
                          )}
                          {booking.end_date && (
                            <Box textAlign="right">
                              <Typography variant="caption" color="text.secondary" display="block">End</Typography>
                              <Typography variant="body2" fontWeight={700} color="text.primary">
                                {new Date(booking.end_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </SurfaceCard>
                    )}

                    {/* Footer row */}
                    <Box display="flex" justifyContent="space-between" sx={{ pt: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block">Booking ID</Typography>
                        <Typography variant="body2" fontWeight={600}>#{booking.id.substring(0, 8)}</Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography variant="caption" color="text.secondary" display="block">Created</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {new Date(booking.created_at).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Inline actions for upcoming / pending bookings */}
                    {(booking.status === 'upcoming' || booking.status === 'pending') && (
                      <Box display="flex" gap={1} mt={1.5}>
                        <Button
                          fullWidth
                          variant="outlined"
                          size="small"
                          startIcon={<EditCalendar />}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRescheduleSheet(booking);
                          }}
                          sx={{ borderRadius: RADIUS.sm, textTransform: 'none', fontWeight: 600 }}
                        >
                          Reschedule
                        </Button>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBooking(booking);
                            setCancelSheetOpen(true);
                          }}
                          sx={{ borderRadius: RADIUS.sm, textTransform: 'none', fontWeight: 600 }}
                        >
                          Cancel
                        </Button>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            showSnackbar('Share feature — Coming Soon!', 'info');
                          }}
                          aria-label="Share booking"
                          sx={{ borderRadius: RADIUS.sm, border: `1px solid ${theme.palette.divider}` }}
                        >
                          <Share />
                        </IconButton>
                      </Box>
                    )}
                  </SurfaceCard>
                </MotionBox>
              ))}
            </AnimatePresence>
          )}
        </MotionBox>
      </Container>

      {/* Detail: BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          maxHeight="92vh"
        >
          {detailContent}
        </BottomSheet>
      ) : (
        <Dialog
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: RADIUS.xl } }}
        >
          {detailContent}
        </Dialog>
      )}

      {/* Cancel confirmation: BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={cancelSheetOpen}
          onClose={() => !cancelling && setCancelSheetOpen(false)}
          title="Cancel Booking?"
          maxHeight="50vh"
        >
          <Typography variant="body2" color="text.secondary" mb={2}>
            Are you sure you want to cancel this booking? This action cannot be undone.
          </Typography>
          {selectedBooking && (
            <Box p={1.5} mb={2} sx={{ bgcolor: 'background.subtle', borderRadius: RADIUS.md }}>
              <Typography variant="subtitle2" fontWeight={600}>{selectedBooking.library?.name}</Typography>
              <Typography variant="caption" color="text.secondary">{selectedBooking.plan?.name}</Typography>
            </Box>
          )}
          <Box display="flex" gap={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setCancelSheetOpen(false)}
              disabled={cancelling}
              sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
            >
              Keep Booking
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleCancelBooking}
              disabled={cancelling}
              sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
            >
              {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
            </Button>
          </Box>
        </BottomSheet>
      ) : (
        <Dialog
          open={cancelSheetOpen}
          onClose={() => !cancelling && setCancelSheetOpen(false)}
          PaperProps={{ sx: { borderRadius: RADIUS.xl } }}
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight={700}>Cancel Booking?</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Are you sure you want to cancel this booking? This action cannot be undone.
            </Typography>
            {selectedBooking && (
              <Box mt={2} p={2} sx={{ bgcolor: 'background.subtle', borderRadius: RADIUS.md }}>
                <Typography variant="subtitle2" fontWeight={600}>{selectedBooking.library?.name}</Typography>
                <Typography variant="caption" color="text.secondary">{selectedBooking.plan?.name}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button
              onClick={() => setCancelSheetOpen(false)}
              disabled={cancelling}
              sx={{ textTransform: 'none', borderRadius: RADIUS.md }}
            >
              Keep Booking
            </Button>
            <Button
              onClick={handleCancelBooking}
              color="error"
              variant="contained"
              disabled={cancelling}
              sx={{ textTransform: 'none', borderRadius: RADIUS.md }}
            >
              {cancelling ? 'Cancelling…' : 'Yes, Cancel'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Reschedule: BottomSheet on mobile, Dialog on desktop */}
      {isMobile ? (
        <BottomSheet
          open={rescheduleSheetOpen}
          onClose={() => !rescheduling && setRescheduleSheetOpen(false)}
          title="Reschedule Booking"
          maxHeight="60vh"
        >
          {rescheduleContent}
        </BottomSheet>
      ) : (
        <Dialog
          open={rescheduleSheetOpen}
          onClose={() => !rescheduling && setRescheduleSheetOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: RADIUS.xl } }}
        >
          <DialogTitle>
            <Typography variant="h6" fontWeight={700}>Reschedule Booking</Typography>
          </DialogTitle>
          <DialogContent>{rescheduleContent}</DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

export default StudentBookings;
